import React, { useEffect, useState } from "react"
import Popup from "component/popup/popup"
import InputText from "component/inputText/inputText"
import Toggle from "component/toggle/Toggle"
import { agent } from "apiClient/api"
import { useSnackbar } from "context/snackbarContext"
import {
    buildAddCityPayload,
    buildUpdateCityPayload,
    cityNameDuplicateError,
    cityNameError,
    ConfigurationCity,
} from "./configurationCities"
import styles from "./configuration.module.scss"

type CityPopupProps = {
    isOpen: boolean
    city: ConfigurationCity | null
    existingCities: ConfigurationCity[]
    onClose: () => void
    onSuccess: () => void
}

const CityPopup: React.FC<CityPopupProps> = ({ isOpen, city, existingCities, onClose, onSuccess }) => {
    const { showSuccess, showError } = useSnackbar()
    const isEdit = Boolean(city)
    const [cityName, setCityName] = useState("")
    const [flagActive, setFlagActive] = useState(true)
    const [nameFieldError, setNameFieldError] = useState<string | undefined>()
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        setCityName(city?.city_name ?? "")
        setFlagActive(city?.flag_active ?? true)
        setNameFieldError(undefined)
    }, [isOpen, city])

    const handleSave = async () => {
        const error = cityNameError(cityName) || cityNameDuplicateError(cityName, existingCities, city?.city_id)
        setNameFieldError(error)
        if (error) return

        setIsSaving(true)
        try {
            const payload = city
                ? buildUpdateCityPayload(city, { cityName, flagActive })
                : buildAddCityPayload({ cityName, flagActive })
            const response = await agent.UpsertCityAPI.put(payload)
            if (response?.status === true) {
                showSuccess(isEdit ? "City updated" : "City added")
                onSuccess()
                onClose()
            } else {
                showError(response?.error?.message || `Failed to ${isEdit ? "update" : "add"} city`)
            }
        } catch (err) {
            console.error("Upsert city error", err)
            showError(`Failed to ${isEdit ? "update" : "add"} city`)
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Popup
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? "Update City" : "Add new city"}
            size="medium"
            secondaryButton={{ label: "Cancel", onClick: onClose }}
            primaryButton={{ label: isSaving ? "Saving..." : "Save", onClick: handleSave, disabled: isSaving }}
            showCloseButton={false}
        >
            <div className={styles.formStack}>
                <InputText
                    label="City name"
                    value={cityName}
                    onChange={(e) => setCityName(e.target.value)}
                    placeholder="Enter city name"
                    error={nameFieldError}
                />
                {!isEdit && (
                    <div className={styles.statusRow}>
                        <div className={styles.statusCopy}>
                            <span className={styles.statusTitle}>Set Status Active</span>
                            <span className={styles.statusHint}>City will be available for selection immediately</span>
                        </div>
                        <Toggle checked={flagActive} onChange={setFlagActive} />
                    </div>
                )}
            </div>
        </Popup>
    )
}

export default CityPopup
